import type { Table } from "@tanstack/react-table";
import { Columns3, FilterX, ListRestart, Search, SquareDashed } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";

export interface TableAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  variant?: "primary" | "secondary" | "danger" | "success";
  /** Mutating action — hidden for read-only USER accounts. */
  write?: boolean;
  /** Needs a selection; disabled until there is one. */
  requiresSelection?: boolean;
  disabled?: boolean;
  /**
   * Renders as a square icon button instead of a labelled one, for toolbars
   * crowded enough that spelling every action out competes with the search
   * box and badges. `icon` is required in practice — the label still backs
   * the button's title/aria-label, so it stays screen-reader visible and
   * still shows on hover.
   */
  iconOnly?: boolean;
}

export interface TableToolbarProps<T> {
  table: Table<T>;
  actions: TableAction[];
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  total: number;
  filtered: number;
  selected: number;
  showFilteredBadge: boolean;
  extraBadges?: ReactNode;
  toolbarExtra?: ReactNode;
  /** Rendered right after the search, before the actions — a view switch that shapes the whole grid. */
  toolbarLead?: ReactNode;
}

/**
 * The `GridMenuBar` equivalent: screen actions on the left, the built-in grid
 * utilities on the right.
 *
 * Write actions disappear for USER-level accounts — the same `WriteGate`
 * behaviour, applied once here rather than per screen. Read-only utilities
 * (search, filters, column presets, exports) stay available to everyone.
 */
export function TableToolbar<T>({
  table,
  actions,
  globalFilter,
  onGlobalFilterChange,
  total,
  filtered,
  selected,
  showFilteredBadge,
  extraBadges,
  toolbarExtra,
  toolbarLead,
}: TableToolbarProps<T>) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const [columnsOpen, setColumnsOpen] = useState(false);

  const visibleActions = actions.filter((action) => !action.write || canWrite);
  const hasFilters = table.getState().columnFilters.length > 0 || globalFilter.length > 0;
  const hasSorting = table.getState().sorting.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-3 py-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-faint" />
        <input
          value={globalFilter}
          onChange={(event) => onGlobalFilterChange(event.target.value)}
          placeholder={t("grid.search", "Search")}
          aria-label={t("grid.search", "Search")}
          className="h-8 w-48 rounded-md border border-line bg-surface pl-7 pr-2 text-[13px] text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-accent max-md:h-10 max-md:w-full"
        />
      </div>

      {toolbarLead}

      {visibleActions.map((action) =>
        action.iconOnly ? (
          <IconButton
            key={action.label}
            label={action.label}
            onClick={action.onClick}
            icon={action.icon}
            variant={action.variant}
            disabled={action.disabled || (action.requiresSelection && selected === 0)}
          />
        ) : (
          <Button
            key={action.label}
            size="sm"
            variant={action.variant ?? "secondary"}
            onClick={action.onClick}
            disabled={action.disabled || (action.requiresSelection && selected === 0)}
            {...(action.icon ? { icon: action.icon } : {})}
          >
            {action.label}
          </Button>
        ),
      )}

      {toolbarExtra}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Badge variant="neutral" size="sm">
          {t("badge.total", "Total: {{count}}", { count: total })}
        </Badge>
        {showFilteredBadge && (
          <Badge variant="accent" size="sm">
            {t("badge.filtered", "Filtered: {{count}}", { count: filtered })}
          </Badge>
        )}
        {selected > 0 && (
          <Badge variant="success" size="sm">
            {t("badge.selected", "Selected: {{count}}", { count: selected })}
          </Badge>
        )}
        {extraBadges}

        <div className="flex items-center gap-1 border-l border-line pl-2">
          {selected > 0 && (
            <IconButton
              label={t("menu.item.grid.deselect", "Deselect all")}
              onClick={() => table.resetRowSelection()}
              icon={<SquareDashed className="size-4" />}
            />
          )}
          {hasFilters && (
            <IconButton
              label={t("menu.item.grid.reset.filters", "Reset filters")}
              onClick={() => {
                table.resetColumnFilters();
                onGlobalFilterChange("");
              }}
              icon={<FilterX className="size-4" />}
            />
          )}
          {hasSorting && (
            <IconButton
              label={t("menu.item.grid.reset.sort", "Reset sort")}
              onClick={() => table.resetSorting()}
              icon={<ListRestart className="size-4" />}
            />
          )}

          <div className="relative">
            <IconButton
              label={t("menu.item.grid.columns", "Columns")}
              onClick={() => setColumnsOpen((open) => !open)}
              icon={<Columns3 className="size-4" />}
              active={columnsOpen}
            />
            {columnsOpen && (
              <>
                <button
                  type="button"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setColumnsOpen(false)}
                />
                <div className="absolute right-0 z-50 mt-1 max-h-80 w-56 overflow-auto rounded-md border border-line bg-surface-over p-1 shadow-overlay">
                  <div className="flex gap-1 border-b border-line p-1">
                    <button
                      type="button"
                      onClick={() => table.toggleAllColumnsVisible(true)}
                      className="flex-1 rounded px-2 py-1 text-[12px] text-ink-muted hover:bg-surface-sunk"
                    >
                      {t("menu.item.grid.view.all", "All")}
                    </button>
                    <button
                      type="button"
                      // Default means the layout as the screen defined it:
                      // which columns show, and in what order.
                      onClick={() => {
                        table.resetColumnOrder();
                        table.setColumnVisibility(
                          Object.fromEntries(
                            table
                              .getAllLeafColumns()
                              .map((column) => [
                                column.id,
                                column.columnDef.meta?.defaultVisible !== false,
                              ]),
                          ),
                        );
                      }}
                      className="flex-1 rounded px-2 py-1 text-[12px] text-ink-muted hover:bg-surface-sunk"
                    >
                      {t("menu.item.grid.view.default", "Default")}
                    </button>
                  </div>
                  {table
                    .getAllLeafColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <label
                        key={column.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] text-ink hover:bg-surface-sunk"
                      >
                        <input
                          type="checkbox"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          className="size-3.5 accent-[var(--accent)]"
                        />
                        {column.columnDef.meta?.headerKey
                          ? t(column.columnDef.meta.headerKey, column.id)
                          : column.id}
                      </label>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tint per action variant — the icon-only equivalent of Button's fills. */
const ICON_TINTS: Record<NonNullable<TableAction["variant"]>, string> = {
  primary: "text-accent hover:bg-accent-soft",
  secondary: "text-ink-faint hover:bg-surface-sunk hover:text-ink",
  danger: "text-risk hover:bg-risk-soft",
  success: "text-ok hover:bg-ok-soft",
};

function IconButton({
  label,
  onClick,
  icon,
  active,
  disabled,
  variant = "secondary",
}: {
  label: string;
  onClick: () => void;
  icon: ReactNode;
  active?: boolean;
  disabled?: boolean;
  variant?: TableAction["variant"];
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        "inline-flex size-8 items-center justify-center rounded-md max-md:size-10",
        "disabled:opacity-50 disabled:pointer-events-none",
        ICON_TINTS[variant ?? "secondary"],
        active && "bg-surface-sunk text-ink",
      )}
    >
      {icon}
    </button>
  );
}
